/**
 * Utility functions, configurations, and definitions for operational services
 * (Empresas de Transporte, Asistentes de Picnic, Bañero) in Cuadro Picnic.
 */

import {
  CuadroPicnicItem,
  PicnicOperationalServiceSummary,
} from '../models/cuadro-picnic.model';

/**
 * Canonical enum matching the category strings used in table columns and queries.
 */
export enum OperationalPicnicServiceCategory {
  TRANSPORT = 'TRANSPORTEPICNIC',
  ASSISTANT = 'ASISTENTEDEPICNIC',
  BATHROOM_ATTENDANT = 'BAÑERO',
  OPERATOR = 'OPERADORPICNIC',
}

/**
 * Union type for modalType and operational category identifiers, supporting
 * enum values, literal strings, and category identifiers.
 */
export type OperationalServiceModalType =
  | OperationalPicnicServiceCategory
  | `${OperationalPicnicServiceCategory}`
  | 'TRANSPORTEPICNIC'
  | 'ASISTENTEDEPICNIC'
  | 'BAÑERO'
  | 'OPERADORPICNIC'
  | (string & {});

/**
 * Specification for querying operational services from Core GraphQL API.
 */
export interface OperationalServiceQueryFilter {
  tipoServicio: string;
  categoriasServicio: string[];
}

/**
 * Descriptor contract centralizing all metadata, data mappings, column bindings,
 * and service query source configurations for an operational service type.
 */
export interface OperationalServiceDescriptor {
  /** Canonical enum identifier */
  category: OperationalPicnicServiceCategory;
  /** Recognized aliases (case-insensitive) for modalType and backward compatibility */
  aliases: string[];
  /** Query filter determining the source of services in Core GraphQL */
  sourceFilter: OperationalServiceQueryFilter;
  /** Modal header title */
  title: string;
  /** Tabler icon name */
  icon: string;
  /** Human-readable label for tooltip and table descriptions */
  label: string;
  /** Property key on CuadroPicnicItem holding the operational summary */
  summaryProperty: keyof Pick<
    CuadroPicnicItem,
    | 'empresaTransporte'
    | 'asistentePicnicServices'
    | 'baneroService'
    | 'operadorPicnicService'
  >;
  /** Fallback properties on raw/legacy API rows */
  legacySummaryProperties?: string[];
  /** Column definition identifier in MatTable */
  columnDef: string;
}

/**
 * Central registry mapping operational service categories to their full descriptors.
 * Serves as the Single Source of Truth for modal types, UI metadata, and backend service sources.
 */
export const OPERATIONAL_SERVICE_CONFIGS: Record<
  OperationalPicnicServiceCategory,
  OperationalServiceDescriptor
> = {
  [OperationalPicnicServiceCategory.TRANSPORT]: {
    category: OperationalPicnicServiceCategory.TRANSPORT,
    aliases: [
      OperationalPicnicServiceCategory.TRANSPORT,
      'TRANSPORTEPICNIC',
      'TRANSPORT',
      'TRANSPORTE',
    ],
    sourceFilter: {
      tipoServicio: 'TRANSPORTE',
      categoriasServicio: ['TRANSPORTEPICNIC'],
    },
    title: 'Empresas de Transporte',
    icon: 'truck',
    label: 'Empresa de Transporte',
    summaryProperty: 'empresaTransporte',
    legacySummaryProperties: ['empresaTransporte'],
    columnDef: OperationalPicnicServiceCategory.TRANSPORT,
  },
  [OperationalPicnicServiceCategory.ASSISTANT]: {
    category: OperationalPicnicServiceCategory.ASSISTANT,
    aliases: [
      OperationalPicnicServiceCategory.ASSISTANT,
      'ASISTENTEDEPICNIC',
      'ASISTENTES_PICNIC',
      'ASISTENTE_PICNIC',
    ],
    sourceFilter: {
      tipoServicio: 'PERSONALDECAMPO',
      categoriasServicio: ['ASISTENTEDEPICNIC'],
    },
    title: 'Asistentes de Picnic',
    icon: 'users',
    label: 'Asistentes de Picnic',
    summaryProperty: 'asistentePicnicServices',
    legacySummaryProperties: ['asistentesPicnic', 'asistentePicnic'],
    columnDef: OperationalPicnicServiceCategory.ASSISTANT,
  },
  [OperationalPicnicServiceCategory.BATHROOM_ATTENDANT]: {
    category: OperationalPicnicServiceCategory.BATHROOM_ATTENDANT,
    aliases: [
      OperationalPicnicServiceCategory.BATHROOM_ATTENDANT,
      'BAÑERO',
      'BANERO',
      'BATHROOM_ATTENDANT',
    ],
    sourceFilter: {
      tipoServicio: 'PERSONALDECAMPO',
      categoriasServicio: ['BAÑERO'],
    },
    title: 'Servicio de Bañero',
    icon: 'pool',
    label: 'Servicio de Bañero',
    summaryProperty: 'baneroService',
    legacySummaryProperties: ['banero'],
    columnDef: OperationalPicnicServiceCategory.BATHROOM_ATTENDANT,
  },
  [OperationalPicnicServiceCategory.OPERATOR]: {
    category: OperationalPicnicServiceCategory.OPERATOR,
    aliases: [
      OperationalPicnicServiceCategory.OPERATOR,
      'OPERADORPICNIC',
      'OPERADOR_PICNIC',
      'OPERADOR',
      'OPERATOR',
    ],
    sourceFilter: {
      tipoServicio: 'OPERADOR',
      categoriasServicio: ['PICNIC'],
    },
    title: 'Operador de Picnic',
    icon: 'briefcase',
    label: 'Operador de Picnic',
    summaryProperty: 'operadorPicnicService',
    legacySummaryProperties: ['operadorPicnic', 'operador'],
    columnDef: OperationalPicnicServiceCategory.OPERATOR,
  },
};

/**
 * Resolves the operational service descriptor for a given category key, modalType, or alias.
 *
 * @param categoryOrModalType - Identifier string, enum value, or alias.
 * @returns Matched OperationalServiceDescriptor or undefined if not recognized.
 */
export function resolveOperationalServiceDescriptor(
  categoryOrModalType?: string | null,
): OperationalServiceDescriptor | undefined {
  if (!categoryOrModalType) {
    return undefined;
  }
  const normalizedKey = categoryOrModalType.trim().toUpperCase();
  const configs = Object.values(OPERATIONAL_SERVICE_CONFIGS);

  const directMatch = configs.find(
    (cfg) => cfg.category.toUpperCase() === normalizedKey,
  );
  if (directMatch) {
    return directMatch;
  }

  return configs.find((cfg) =>
    cfg.aliases.some((alias) => alias.toUpperCase() === normalizedKey),
  );
}

/**
 * Resolves the operational service query filters for Core GraphQL.
 * If a specific modalType is provided, returns its configured filter.
 * If not provided (or unknown), returns all configured filters combined.
 *
 * @param modalType - Optional modal category identifier.
 * @returns Array of operational filters matching GraphQL input schema.
 */
export function resolveOperationalServiceFilters(
  modalType?: string | null,
): OperationalServiceQueryFilter[] {
  const descriptor = resolveOperationalServiceDescriptor(modalType);
  if (descriptor) {
    return [{ ...descriptor.sourceFilter }];
  }

  // Combined fallback: aggregate all configured filters grouped by tipoServicio
  const filtersMap = new Map<string, Set<string>>();
  for (const config of Object.values(OPERATIONAL_SERVICE_CONFIGS)) {
    const { tipoServicio, categoriasServicio } = config.sourceFilter;
    if (!filtersMap.has(tipoServicio)) {
      filtersMap.set(tipoServicio, new Set());
    }
    const catSet = filtersMap.get(tipoServicio)!;
    categoriasServicio.forEach((c) => catSet.add(c));
  }

  return Array.from(filtersMap.entries()).map(
    ([tipoServicio, categoriasSet]) => ({
      tipoServicio,
      categoriasServicio: Array.from(categoriasSet),
    }),
  );
}

/**
 * Resolves the CSS status class for an operational service count pill based on OS generation completion.
 *
 * @param serviceSummary - Operational service summary containing totalCount and generatedOrdersCount.
 * @returns CSS modifier class name ('os-status-complete', 'os-status-partial', 'os-status-pending', or '').
 */
export function resolveServicePillStatusClass(
  serviceSummary: PicnicOperationalServiceSummary | null | undefined,
): string {
  if (!serviceSummary || serviceSummary.totalCount === 0) {
    return '';
  }
  const total = serviceSummary.totalCount;
  const generated = serviceSummary.generatedOrdersCount;
  const preliminary = serviceSummary.preliminaryOrdersCount ?? 0;

  if (generated === total) {
    // Verde solo si todas están confirmadas (ninguna preliminar).
    return preliminary > 0 ? 'os-status-preliminary' : 'os-status-complete';
  }
  if (generated > 0) {
    return preliminary > 0 ? 'os-status-preliminary' : 'os-status-partial';
  }
  return 'os-status-pending';
}

/** Todas las OS generadas y ninguna preliminar → check verde. */
export function isOperationalSummaryFullyConfirmed(
  serviceSummary: PicnicOperationalServiceSummary | null | undefined,
): boolean {
  if (!serviceSummary || serviceSummary.totalCount === 0) {
    return false;
  }
  return (
    serviceSummary.generatedOrdersCount === serviceSummary.totalCount &&
    (serviceSummary.preliminaryOrdersCount ?? 0) === 0
  );
}

/** Hay OS preliminar (con o sin otras confirmadas / pendientes). */
export function hasOperationalSummaryPreliminary(
  serviceSummary: PicnicOperationalServiceSummary | null | undefined,
): boolean {
  return (serviceSummary?.preliminaryOrdersCount ?? 0) > 0;
}

/**
 * Resolves descriptive tooltip text for the operational service count pill.
 *
 * @param categoryName - Human-readable category title.
 * @param serviceSummary - Operational service summary containing counts.
 * @returns Contextual tooltip description.
 */
export function resolveServicePillTooltip(
  categoryName: string,
  serviceSummary: PicnicOperationalServiceSummary | null | undefined,
): string {
  if (!serviceSummary || serviceSummary.totalCount === 0) {
    return `${categoryName}: Sin servicios asignados`;
  }
  const totalCount = serviceSummary.totalCount;
  const generatedOrdersCount = serviceSummary.generatedOrdersCount;
  const pendingOrdersCount =
    serviceSummary.pendingOrdersCount ?? totalCount - generatedOrdersCount;

  const preliminaryOrdersCount = serviceSummary.preliminaryOrdersCount ?? 0;

  if (generatedOrdersCount === totalCount && preliminaryOrdersCount === 0) {
    return `${categoryName} (${totalCount}): ${totalCount} de ${totalCount} con OS confirmada (Completo) — Clic para abrir detalle`;
  }
  if (generatedOrdersCount === totalCount && preliminaryOrdersCount > 0) {
    return `${categoryName} (${totalCount}): ${preliminaryOrdersCount} preliminar${preliminaryOrdersCount === 1 ? '' : 'es'} de ${totalCount} — Confirma para completar — Clic para abrir`;
  }
  if (preliminaryOrdersCount > 0) {
    return `${categoryName} (${totalCount}): ${generatedOrdersCount} de ${totalCount} con OS (${preliminaryOrdersCount} preliminar${preliminaryOrdersCount === 1 ? '' : 'es'}, ${pendingOrdersCount} pendiente${pendingOrdersCount === 1 ? '' : 's'}) — Clic para abrir`;
  }
  return `${categoryName} (${totalCount}): ${generatedOrdersCount} de ${totalCount} con Orden de Servicio (${pendingOrdersCount} pendiente${pendingOrdersCount === 1 ? '' : 's'}) — Clic para abrir y gestionar`;
}

/**
 * Resolves modal title and Tabler icon matching the operational service category.
 * Defaults to 'Servicio' and 'briefcase' if the category is unhandled or generic.
 *
 * @param categoryOrModalType - Category identifier string or modalType.
 * @param customTitle - Optional explicit custom title override.
 * @param customIcon - Optional explicit custom icon override.
 * @returns Object with title and icon name.
 */
export function resolveOperationalServiceMetadata(
  categoryOrModalType?: string | null,
  customTitle?: string,
  customIcon?: string,
): { title: string; icon: string } {
  if (customTitle && customIcon) {
    return { title: customTitle, icon: customIcon };
  }

  const descriptor = resolveOperationalServiceDescriptor(categoryOrModalType);
  return {
    title: customTitle || descriptor?.title || 'Servicio',
    icon: customIcon || descriptor?.icon || 'briefcase',
  };
}

/**
 * Resolves the original operational service summary from a row item.
 *
 * @param row - CuadroPicnicItem row record.
 * @param categoryOrModalType - Category identifier or modalType.
 * @returns Original summary or undefined.
 */
export function resolveOriginalServiceSummary(
  row: CuadroPicnicItem | null | undefined,
  categoryOrModalType?: string | null,
): PicnicOperationalServiceSummary | undefined {
  if (!row) {
    return undefined;
  }

  const descriptor = resolveOperationalServiceDescriptor(categoryOrModalType);
  if (descriptor) {
    const summary = row[descriptor.summaryProperty];
    if (summary) {
      return summary;
    }
    const untypedRow = row as Record<string, any>;
    if (descriptor.legacySummaryProperties) {
      for (const prop of descriptor.legacySummaryProperties) {
        if (untypedRow[prop]) {
          return untypedRow[prop];
        }
      }
    }
  }

  return undefined;
}

/**
 * Synchronizes the updated operational service summary across all table rows
 * sharing the same activity identifier or itinerary + date.
 *
 * @param items - Master list of CuadroPicnicItem records.
 * @param targetRowItem - Selected row item.
 * @param categoryOrModalType - Operational service category or modalType.
 * @param updatedSummary - Newly computed or updated operational service summary.
 * @returns New immutable array with updated rows.
 */
export function synchronizeOperationalServiceSummary(
  items: CuadroPicnicItem[],
  targetRowItem: CuadroPicnicItem,
  categoryOrModalType: string,
  updatedSummary: PicnicOperationalServiceSummary,
): CuadroPicnicItem[] {
  const targetActivityId = targetRowItem.idActivity;
  const targetItineraryId = targetRowItem.idItinerary;
  const safeSummary = { ...updatedSummary };
  const descriptor = resolveOperationalServiceDescriptor(categoryOrModalType);

  return items.map((itemRow) => {
    const isSameActivity =
      itemRow.id === targetRowItem.id ||
      (Boolean(targetActivityId) && itemRow.idActivity === targetActivityId) ||
      (!targetActivityId &&
        itemRow.idItinerary === targetItineraryId &&
        itemRow.fechaOperacion === targetRowItem.fechaOperacion);

    if (!isSameActivity) {
      return itemRow;
    }

    const currentSummary = resolveOriginalServiceSummary(
      itemRow,
      categoryOrModalType,
    );
    const effectiveTotalCount =
      safeSummary.totalCount > 0
        ? safeSummary.totalCount
        : (currentSummary?.totalCount ?? 0);

    const effectiveSummary: PicnicOperationalServiceSummary = {
      totalCount: effectiveTotalCount,
      generatedOrdersCount: safeSummary.generatedOrdersCount,
      pendingOrdersCount: Math.max(
        0,
        effectiveTotalCount - safeSummary.generatedOrdersCount,
      ),
      preliminaryOrdersCount: safeSummary.preliminaryOrdersCount ?? 0,
    };

    if (descriptor) {
      return {
        ...itemRow,
        [descriptor.summaryProperty]: effectiveSummary,
      };
    }

    return itemRow;
  });
}
